import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./SuperChatAIOrderTable.scss";
import { fetchOrderById } from "../../../../../store/slices/ordersSlice";

const getPriorityRank = (priority) => {
  const normalizedPriority = String(priority || "")
    .trim()
    .toUpperCase();

  const numericMatch = normalizedPriority.match(/^P(\d+)$/);
  if (numericMatch) {
    return Number.parseInt(numericMatch[1], 10);
  }

  const namedPriorityRank = {
    HIGH: 1,
    MEDIUM: 2,
    LOW: 3,
  };

  return namedPriorityRank[normalizedPriority] ?? Number.MAX_SAFE_INTEGER;
};

export const SuperChatAIOrderTable = ({
  orders,
  onAddUserMessage,
  actionNeededCount,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedRole = useSelector((state) => state.user?.selectedRole);

  if (!orders || orders.length === 0) return null;

  // Define the exact column order we want to display
  const columnOrder = [
    "priority",
    "axonId",
    "status",
    "subStatus",
    "clientEntity",
    "role",
    "analyzer",
    "superRegion",
    "region",
    "dueDate",
    "agingInDays",
    // "varianceReason",
  ];

  // Filter columns to only include those that exist in the order data
  // Use 'in' operator or check if value is not undefined to handle different object types
  const columns = columnOrder.filter(
    (col) =>
      col !== "guid" && (col in orders[0] || orders[0][col] !== undefined),
  );

  const sortedOrders = [...orders].sort((leftOrder, rightOrder) => {
    const priorityDifference =
      getPriorityRank(leftOrder?.priority) -
      getPriorityRank(rightOrder?.priority);

    if (priorityDifference !== 0) {
      return priorityDifference;
    }

    return 0;
  });

  // Create a mapping for display names
  const getDisplayName = (key) => {
    const displayNames = {
      priority: "Priority",
      axonId: "Axon ID",
      status: "Status",
      subStatus: "Sub Status",
      clientEntity: "Client-Entity",
      role: "Role",
      analyzer: "Analyzer",
      superRegion: "Super Region",
      region: "Region",
      dueDate: "Due Date",
      agingInDays: "Ageing In (By Status)",
      // varianceReason: "Reason for Variance",
    };
    return displayNames[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const handleOrderClick = (order) => {
    //   console.log("Order clicked - navigating to details:", order.guid);

    // Dispatch to fetch order data
    dispatch(
      fetchOrderById({
        orderId: order.guid,
        roleId: selectedRole?.roleId || "",
      }),
    );

    // Navigate directly to the order details page
    // Pass a unique superAgentNavigationId to force fresh query API call each time
    // The order details page Chatbot will automatically call the query API to fetch the summary
    navigate(`/orders/${order.guid}`, {
      state: {
        orderDetails: order,
        superAgentNavigationId: Date.now(),
      },
    });
  };

  return (
    <div className="super-agent-order-table-wrapper">
      <div className="super-agent-order-table">
        <div className="super-agent-order-header">
          {columns.map((column) => (
            <span
              key={column}
              className={`super-agent-header-cell ${column} ${
                column === "agingInDays" ? "two-line-header" : ""
              }`}
            >
              {column === "agingInDays" ? (
                <>
                  <span className="super-agent-header-label">Ageing In</span>
                  <span className="super-agent-header-sub-label">
                    (By Status)
                  </span>
                </>
              ) : (
                getDisplayName(column)
              )}
            </span>
          ))}
        </div>
        {sortedOrders.map((order) => (
          <div key={order.guid} className="super-agent-order-row">
            {columns.map((column) => {
              const value = order[column];
              const isAxonId = column === "axonId";
              const isFeedback = column === "feedback";

              return (
                <span
                  key={column}
                  className={`super-agent-cell ${column} ${
                    isAxonId ? "super-agent-order-link" : ""
                  } ${
                    isFeedback
                      ? `super-agent-feedback super-agent-feedback-${value.toLowerCase()}`
                      : ""
                  }`}
                  onClick={isAxonId ? () => handleOrderClick(order) : undefined}
                  title={value}
                >
                  {isAxonId ? `AX-${value}` : value}
                </span>
              );
            })}
          </div>
        ))}
      </div>
      {actionNeededCount > 0 && (
        <div className="super-agent-action-needed-message">
          However there {actionNeededCount === 1 ? "is" : "are"}{" "}
          {actionNeededCount} order{actionNeededCount === 1 ? "" : "s"} where
          the User's action is needed.
        </div>
      )}
    </div>
  );
};
